/**
 * @file        NewBookingFlowPage.jsx
 * @module      pages/NewBookingFlowPage
 * @description Page-level container for the booking flow. Renders the booking
 *              summary header and hosts the {@link NewBookingFlowContent} component
 *              where the user reviews and confirms a selected freight rate.
 *              The data driving this page (the chosen rate and any surcharge
 *              selections) is passed in via React Router's navigation state from
 *              the search results screen rather than fetched here.
 * @author      rahul.kumar@team.sgate.in
 * @date        2026-06-17
 */

import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import NewBookingFlowContent from "../components/common/PaymentSection/NewBookingFlowContent";
import { clearSearchRateAvailableData } from "../store/SearchRates/SearchRatesSlice";
import useLogCreditLimit from "../utils/useLogCreditLimit";

/**
 * NewBookingFlowPage
 *
 * Top-level page component for the booking summary / new booking flow.
 *
 * Reads the rate the user selected and their surcharge choices from the router
 * location state, then delegates the actual booking UI to NewBookingFlowContent.
 * Acts as a guard: if the page is reached without a selected rate (e.g. a direct
 * URL hit or a page refresh that wipes router state), it redirects back to search.
 *
 * @returns {JSX.Element|null} The booking summary page, or `null` while redirecting
 *                             when no rate was selected.
 */
const NewBookingFlowPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Rate the user picked on the search results page, forwarded via router state.
  const selectedItem = location.state?.selectedItem;
  console.log(selectedItem,"selectedItem");
  // Optional surcharges the user opted into for the selected rate.
  const surchargeSelection = location.state?.surchargeSelection;
  // allowed_airline_list/35 rows fetched alongside ratesearch/all on the results page.
  const allowedAirlines = location.state?.allowedAirlines;
  // Logs the user's credit limit for this search's mode (air/ocean).
  useLogCreditLimit({ enabled: !!selectedItem });

  /**
   * Navigates back to the previous (search results) screen.
   *
   * Clears the cached available-rates data first so the user returns to a clean
   * search state instead of stale results that may no longer be bookable.
   *
   * @returns {void}
   */
  const handleBack = () => {
    dispatch(clearSearchRateAvailableData());
    navigate(-1);
  };

  // Guard: without a selected rate there is nothing to book — this happens on a
  // direct navigation or a refresh that drops router state. Redirect to search
  // (replace so this dead page isn't left in the history stack) and render nothing.
  if (!selectedItem) {
    navigate("/search-rates", { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen w-full bg-[#F8F9FA]">
      {/* Sticky header: stays pinned to the top so the back button and summary
          title remain accessible while the user scrolls the booking content. */}
      <div className="sticky top-0 z-10 flex items-center gap-3 px-6 py-4 bg-white border-b border-gray-200 shadow-sm">
        <button
          className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors shrink-0"
          onClick={handleBack}
          title="Go back to search results"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-gray-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <div className="flex items-center gap-3">
          {/* Show the carrier logo only when the first itinerary actually
              provides one — many rates ship without a logo URL. */}
          {selectedItem?.flightItineraries?.[0]?.ailinelogoUrl && (
            <img
              src={selectedItem.flightItineraries[0].ailinelogoUrl}
              alt="airline"
              className="h-6 w-auto object-contain"
            />
          )}
          <h1 className="text-lg font-bold text-gray-800">Booking Summary</h1>
        </div>
      </div>

      {/* Main content: the booking flow itself, fed the selected rate and the
          user's surcharge choices so it can render the summary and payment steps. */}
      <div className="w-full">
        <NewBookingFlowContent
          selectedItem={selectedItem}
          surchargeSelection={surchargeSelection}
          allowedAirlines={allowedAirlines}
        />
      </div>
    </div>
  );
};

export default NewBookingFlowPage;
